import { api } from '@/lib/http-client';
import type { QueryParams } from '@/types/api';
import type {
  CreateTemplateRequest,
  DeleteTemplateResult,
  PlantillaFiltros,
  PreviewTemplateRequest,
  PreviewTemplateResult,
  SeedTemplatesRequest,
  SeedTemplatesResult,
  SendTemplateResult,
  SendTestRequest,
  SyncTemplatesResult,
  TemplateCatalog,
  UpdateTemplateRequest,
  WhatsappTemplate,
} from '@/types/templates';

/**
 * Módulo 8 — Plantillas de WhatsApp (`docs/openapi.yaml` → tag `WhatsappTemplates`,
 * `backend/src/modules/whatsapp-templates/whatsapp-templates.routes.ts`).
 *
 * Qué puede cada rol (lo aplica el backend, no la pantalla):
 *  - leer el listado, el detalle, el catálogo de eventos y previsualizar → cualquier miembro
 *    (`requireTenant`);
 *  - crear, editar, borrar, **sembrar las recomendadas**, enviar a Meta, sincronizar el estado y
 *    mandar una prueba → solo ADMIN (`requireRole('ADMIN')`, 403 si alguien invoca el endpoint a
 *    mano).
 *
 * Cuatro cosas del contrato que no se pueden adivinar:
 *  - el listado devuelve un ARRAY pelado (`WhatsappTemplate[]`), no `{ items, total }` como Campañas,
 *    y filtra en el servidor por `evento`, `categoria` y `estado`;
 *  - `GET .../eventos` es el catálogo (rutas fijas van antes de `/:templateId` en Express): trae la
 *    definición de los 9 eventos del sistema, los campos que puede usar cada uno y si el negocio ya
 *    tiene su plantilla;
 *  - `POST .../recomendadas` siembra los borradores de TODOS los eventos que falten (es idempotente de
 *    facto: los que ya existen vuelven en `existentes`) y `POST .../sincronizar` trae el estado real
 *    del WABA (sin credenciales de WhatsApp responde 400);
 *  - el `DELETE` no devuelve un booleano como en Productos: responde `{ id, eliminadaEnMeta }`, y con
 *    `?eliminarEnMeta=true` borra además la plantilla del WABA (400 si la local sigue en BORRADOR).
 */

/**
 * Filtros → query params, sin mandar los que no van.
 *
 * Los tres son filtros REALES del backend (`listTemplatesQuerySchema`) y el mismo objeto formado acá
 * es el que entra en la query key, así «filtro distinto» = «entrada de caché distinta».
 */
export function templatesQueryFrom(filters: PlantillaFiltros = {}): QueryParams {
  const query: QueryParams = {};

  if (filters.evento) query.evento = filters.evento;
  if (filters.categoria) query.categoria = filters.categoria;
  if (filters.estado) query.estado = filters.estado;

  return query;
}

/**
 * `GET /api/tenants/:tenantId/whatsapp-templates` — todas las plantillas del negocio.
 *
 * Ordena por `categoria`, `evento` e `idioma` (las del cliente primero) y no pagina: el negocio tiene
 * una plantilla por evento e idioma, así que la lista es corta y se lee entera.
 */
export function listTemplates(
  tenantId: string,
  filters: PlantillaFiltros = {},
): Promise<WhatsappTemplate[]> {
  return api.get<WhatsappTemplate[]>(`/api/tenants/${tenantId}/whatsapp-templates`, {
    query: templatesQueryFrom(filters),
  });
}

/** `GET /api/tenants/:tenantId/whatsapp-templates/:templateId` — 404 si no es de este negocio. */
export function fetchTemplate(tenantId: string, templateId: string): Promise<WhatsappTemplate> {
  return api.get<WhatsappTemplate>(
    `/api/tenants/${tenantId}/whatsapp-templates/${templateId}`,
  );
}

/**
 * `GET /api/tenants/:tenantId/whatsapp-templates/eventos` — catálogo de eventos del sistema.
 *
 * Cada entrada trae el texto sugerido, el encabezado, los campos disponibles y los `parametros`
 * sugeridos, más el resumen de la plantilla del negocio para ese evento (`null` si todavía no hay).
 * Es lo que usa el onboarding y el formulario de alta para no inventar textos ni campos.
 */
export function listEventCatalog(tenantId: string): Promise<TemplateCatalog> {
  return api.get<TemplateCatalog>(`/api/tenants/${tenantId}/whatsapp-templates/eventos`);
}

/** `POST /api/tenants/:tenantId/whatsapp-templates` (solo ADMIN) — responde 201 con la fila creada. */
export function createTemplate(
  tenantId: string,
  payload: CreateTemplateRequest,
): Promise<WhatsappTemplate> {
  return api.post<WhatsappTemplate>(`/api/tenants/${tenantId}/whatsapp-templates`, payload);
}

/**
 * `POST /api/tenants/:tenantId/whatsapp-templates/recomendadas` (solo ADMIN) — onboarding rápido.
 *
 * Crea con los textos del catálogo un borrador por cada evento que falte (los que ya existen vuelven
 * en `existentes`) y devuelve el catálogo actualizado. Con `enviarAMeta: true` además los manda a
 * revisión de Meta: las de encabezado IMAGEN fallan ahí (el módulo no sube archivos) y quedan
 * reportadas en `errores` con su borrador ya creado. Sin canal de WhatsApp conectado responde 400 y
 * NINGÚN borrador se crea.
 */
export function seedRecommendedTemplates(
  tenantId: string,
  payload: SeedTemplatesRequest = {},
): Promise<SeedTemplatesResult> {
  return api.post<SeedTemplatesResult>(
    `/api/tenants/${tenantId}/whatsapp-templates/recomendadas`,
    payload,
  );
}

/** `PATCH /api/tenants/:tenantId/whatsapp-templates/:templateId` (solo ADMIN). */
export function updateTemplate(
  tenantId: string,
  templateId: string,
  payload: UpdateTemplateRequest,
): Promise<WhatsappTemplate> {
  return api.patch<WhatsappTemplate>(
    `/api/tenants/${tenantId}/whatsapp-templates/${templateId}`,
    payload,
  );
}

/**
 * `DELETE /api/tenants/:tenantId/whatsapp-templates/:templateId` (solo ADMIN).
 *
 * `eliminarEnMeta` viaja como `'true'` en la query (el schema lo valida con `z.enum(['true','false'])`);
 * el backend responde 400 si se pide borrar en Meta una plantilla que nunca se envió (BORRADOR) y 400
 * si el negocio no tiene WhatsApp conectado.
 */
export function deleteTemplate(
  tenantId: string,
  templateId: string,
  eliminarEnMeta = false,
): Promise<DeleteTemplateResult> {
  return api.delete<DeleteTemplateResult>(
    `/api/tenants/${tenantId}/whatsapp-templates/${templateId}`,
    { query: eliminarEnMeta ? { eliminarEnMeta: 'true' } : undefined },
  );
}

/**
 * `POST /api/tenants/:tenantId/whatsapp-templates/:templateId/preview` — cuerpo renderizado.
 *
 * Lo puede llamar cualquier miembro (no es escritura). Sin body usa el cuerpo y el mapeo guardados y
 * rellena los valores que falten con el `ejemplo` del campo; también acepta `cuerpo`/`parametros` para
 * ver un texto antes de guardarlo. Valida el mapeo igual que al guardar (400 si no coincide con las
 * variables del cuerpo).
 */
export function previewTemplate(
  tenantId: string,
  templateId: string,
  payload: PreviewTemplateRequest = {},
): Promise<PreviewTemplateResult> {
  return api.post<PreviewTemplateResult>(
    `/api/tenants/${tenantId}/whatsapp-templates/${templateId}/preview`,
    payload,
  );
}

/**
 * `POST .../:templateId/enviar-meta` (solo ADMIN) — crea la plantilla en el WABA para que Meta la
 * revise (queda `PENDIENTE` y `activo = false`).
 *
 * 400 si ya está APROBADA, 409 si ya está PENDIENTE, 400 si tiene encabezado de IMAGEN (esas se crean
 * en WhatsApp Manager) y 400 si el negocio no tiene WhatsApp conectado. Si Meta la rechaza, el
 * borrador local queda intacto para corregirlo y reintentar.
 */
export function submitTemplateToMeta(
  tenantId: string,
  templateId: string,
): Promise<WhatsappTemplate> {
  return api.post<WhatsappTemplate>(
    `/api/tenants/${tenantId}/whatsapp-templates/${templateId}/enviar-meta`,
  );
}

/**
 * `POST .../:templateId/sincronizar` (solo ADMIN) — trae el estado real de ESA plantilla del WABA.
 *
 * 404 si Meta no tiene una plantilla con ese nombre e idioma (nunca se envió o se borró allá) y 400
 * si el negocio no tiene WhatsApp conectado.
 */
export function syncTemplate(tenantId: string, templateId: string): Promise<WhatsappTemplate> {
  return api.post<WhatsappTemplate>(
    `/api/tenants/${tenantId}/whatsapp-templates/${templateId}/sincronizar`,
  );
}

/**
 * `POST /api/tenants/:tenantId/whatsapp-templates/sincronizar` (solo ADMIN) — sincroniza TODAS con
 * una sola consulta a Meta.
 *
 * Devuelve cuántas revisó, cuántas cambiaron de estado y los nombres locales que Meta no tiene, más el
 * catálogo actualizado.
 */
export function syncTemplates(tenantId: string): Promise<SyncTemplatesResult> {
  return api.post<SyncTemplatesResult>(`/api/tenants/${tenantId}/whatsapp-templates/sincronizar`);
}

/**
 * `POST .../:templateId/prueba` (solo ADMIN) — **envío REAL** por WhatsApp al número indicado.
 *
 * Solo sirve con una plantilla APROBADA por Meta (400 con el estado actual si no lo está) y con
 * `headerImageUrl` cuando el encabezado es de tipo IMAGEN. Si Meta rechaza el envío el backend
 * responde 400 con el motivo (`sin_credenciales`, `envio_fallido`…): la pantalla lo muestra tal cual.
 */
export function sendTemplateTest(
  tenantId: string,
  templateId: string,
  payload: SendTestRequest,
): Promise<SendTemplateResult> {
  return api.post<SendTemplateResult>(
    `/api/tenants/${tenantId}/whatsapp-templates/${templateId}/prueba`,
    payload,
  );
}

